<template>
  <div class="pdi-wrap">
    <div v-if="pending" class="alert alert-secondary outline" role="alert" style="float: right; font-size: 12px; padding: 2px 10px; margin: 0;">Actualizando CFDIs...</div>

    <button v-else class="btn btn-secondary btn-xs" type="button" style="float: right;" @click="download" :disabled="isChecking" :title="isChecking ? 'Actualizando CFDIs' : 'Actualizar CFDIs'">
      <i class="fa fa-refresh"></i> Descargar CFDIs
    </button>

    <button class="btn btn-secondary btn-xs" type="button" style="float: right;" @click="synchronize" :disabled="isChecking" :title="'Validar CFDIs'">
      <i class="fa fa-refresh"></i> Validar CFDIs
    </button>
  </div>
</template>

<script>
import ws from "@/services/cfdis";
import accountService from '@/services/account';
import dashboardService from '@/services/dashboard';

export default {
  name: "PendingDownloadsIndicator",
  props: {
    rfc: { type: String, required: true },
    period: { type: String, required: true },
    nature: { type: String, required: false, default: "received" },
    intervalMs: { type: Number, default: 30000 },
    autoStart: { type: Boolean, default: false },
  },
  emits: ["change", "finished", "error"],
  data() {
    return {
      pending: false,
      isChecking: false,
      _timer: null,
      _lastPending: null,
    };
  },
  watch: {
    rfc() {
      this.check(true);
    },
    nature() {
      this.check(true);
    },
    period() {
      this.check(true);
    },
  },
  mounted() {
    if (this.autoStart) this.start();
  },
  beforeUnmount() {
    this.stop();
  },
  methods: {
    async ensureDownloadsEnabled() {
      try {
        const response = await accountService.getSubscription();
        const status = response?.data?.status || null;

        const history = await dashboardService.getDownloadCenterHistory(this.rfc, 1, 1, { skipLoading: true });
        const efirmaIncident = history?.efirma_incident || null;

        if (efirmaIncident) {
          this.$toast.error(efirmaIncident?.warning_message || 'Las descargas de CFDIs están suspendidas hasta actualizar la e.firma.');
          return false;
        }

        if (status && !status.can_request_downloads) {
          this.$toast.error(status?.warning?.message || 'Tu suscripción no está vigente. Renueva tu paquete para reactivar descargas.');
          return false;
        }
      } catch (error) {
        // If validation call fails, backend will still enforce the rule.
      }

      return true;
    },
    start() {
      this.stop();
      this.check(true);
      this._timer = setInterval(() => this.check(), this.intervalMs);
    },
    stop() {
      if (this._timer) {
        clearInterval(this._timer);
        this._timer = null;
      }
    },
    restart() {
      this.pending = true;
    },
    async download() {
      const enabled = await this.ensureDownloadsEnabled();
      if(!enabled){
        return;
      }

        let response = await ws.downloadCfdis(this.rfc, this.period, this.nature); 

        if(response && response.type == "success"){
            this._lastPending = true;
            this.pending = true;

            this.$toast.success(response.message);
        }
        else{
            this.$toast.error(response.message);
        }
    },
    async synchronize() {
      const enabled = await this.ensureDownloadsEnabled();
      if(!enabled){
        return;
      }

        let response = await ws.synchronizeCfdis(this.rfc, this.period, this.nature); 

        if(response && response.type == "success"){
            this.$toast.success(response.message);
        }
        else{
            this.$toast.error(response.message);
        }
    },
    async check(force = false) {
      if (!this.pending) return;
      if (this.isChecking && !force) return;
      if (!this.rfc || !this.period || !this.nature) return;

      this.isChecking = true;
      try {
        const res = await ws.checkDownloads(this.rfc, this.period, this.nature);

        if (res && res.type === "success") {
          const next = !!res.downloads; // true = pendientes, false = listo

          // Emitir cuando cambia
          if (this._lastPending === null || next !== this._lastPending) {
            this.$emit("change", next);

            // Emitir SOLO cuando cambie a false
            if (this._lastPending === true && next === false) {
              this.$toast.success("La actualización de CFDIs ha finalizado.");
              this.$emit("finished");
            }

            this._lastPending = next;
          }

          this.pending = next;
        }
      } catch (e) {
        this.$emit("error", e);
      } finally {
        this.isChecking = false;
      }
    },
  },
};
</script>

<style scoped>
.pdi-wrap{
  display: inline-block;
}
</style>
